import { cn } from '@jeraisonne/ui';
import { GraduationCap } from 'lucide-react';
import type { AppObjectiveTemplate } from '@/lib/student-objectives/objective-types';

interface Props {
  template: AppObjectiveTemplate;
  isSelected: boolean;
  onSelect: () => void;
  isDisabled?: boolean;
}

export function ObjectiveCard({ template, isSelected, onSelect, isDisabled = false }: Props) {
  const isEpreuve = template.contentType === 'epreuves';
  const illustrationUrl = template.illustrationUrl ?? null;
  const hasIllustration = !!illustrationUrl;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isDisabled}
      className={cn(
        'relative w-full overflow-hidden rounded-xl border-2 text-left transition-all',
        'focus-visible:ring-primary-500 focus-visible:outline-none focus-visible:ring-2',
        'active:scale-[0.98]',
        hasIllustration ? 'min-h-56 sm:min-h-64' : 'min-h-18 p-4',
        isSelected
          ? 'border-primary-500 bg-primary-50 shadow-md'
          : 'border-ink-100 hover:border-ink-300 bg-white',
        isDisabled && 'cursor-not-allowed opacity-60',
      )}
    >
      {hasIllustration && (
        <>
          <img
            src={illustrationUrl!}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
          <div
            aria-hidden="true"
            className={cn(
              'bg-linear-to-b pointer-events-none absolute inset-0',
              isSelected
                ? 'via-primary-50/60 to-primary-50/95 from-transparent'
                : 'via-ink-50/60 to-ink-50/95 from-transparent',
            )}
          />
        </>
      )}
      {isSelected && (
        <span className="bg-primary-500 absolute right-3 top-3 z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full shadow">
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path
              d="M1 4L3.5 6.5L9 1"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
      <div
        className={cn('min-w-0', hasIllustration ? 'absolute inset-x-0 bottom-0 p-4' : 'relative')}
      >
        <p className="text-ink-900 text-sm font-semibold leading-tight">{template.title}</p>
        {isEpreuve && (
          <span className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
            <GraduationCap className="h-3 w-3" aria-hidden="true" />
            Épreuves
          </span>
        )}
        <p className="text-ink-500 mt-1 text-xs leading-relaxed">{template.description}</p>
      </div>
    </button>
  );
}
